import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, Grid, List, ArrowRight, Sparkles, MessageSquare } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import Breadcrumbs from '../components/Breadcrumbs';
import {
  promptsData,
  promptCategories,
  searchPrompts,
  getPromptsByCategory
} from '../data/promptsData';

const PromptsPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [viewMode, setViewMode] = useState('grid');
  const [filteredPrompts, setFilteredPrompts] = useState(promptsData);

  useEffect(() => {
    let prompts = promptsData;

    if (selectedCategory !== 'All') {
      prompts = getPromptsByCategory(selectedCategory);
    }

    if (searchQuery) {
      prompts = searchPrompts(searchQuery);
    }

    setFilteredPrompts(prompts);
  }, [searchQuery, selectedCategory]);

  const breadcrumbItems = [
    { label: 'Prompts', href: null }
  ];

  const promptsSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Sortio Prompt Library",
    "description": "Ready-to-use AI prompts for organizing files, documents, and digital assets. Copy and paste these prompts into Sortio for instant file organization.",
    "url": "https://www.getsortio.com/prompts",
    "publisher": {
      "@type": "Organization",
      "name": "Sortio",
      "url": "https://www.getsortio.com"
    },
    "numberOfItems": promptsData.length,
    "hasPart": promptsData.map(prompt => ({
      "@type": "HowTo",
      "name": prompt.title,
      "description": prompt.shortDescription,
      "url": `https://www.getsortio.com/prompts/${prompt.slug}`
    }))
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('All');
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#121212" }}>
      <SEOHead
        title="AI File Organization Prompts | Sortio Prompt Library"
        description="Ready-to-use AI prompts for organizing files, documents, photos, and downloads. Copy and paste these prompts into Sortio for instant file organization."
        canonical="https://www.getsortio.com/prompts"
        schema={promptsSchema}
        keywords="AI file organization prompts, file sorting prompts, document organization prompts, Sortio prompts, file management AI prompts"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Breadcrumbs items={breadcrumbItems} />

        {/* Header Section */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <div className="flex items-center justify-center p-1.5 rounded-full bg-sortio-secondary/10 px-4 py-1.5">
              <MessageSquare className="mr-2 text-sortio-secondary" size={14} />
              <span className="text-xs font-medium uppercase tracking-wider text-sortio-secondary">Prompt Library</span>
            </div>
          </div>
          <h1 className="text-5xl md:text-6xl font-semibold mb-6 tracking-tight text-apple-50">
            File Organization <span className="apple-gradient-text">Prompts</span>
          </h1>
          <p className="text-apple-300 text-xl max-w-3xl mx-auto font-light leading-relaxed">
            Ready-to-use AI prompts to organize your files, documents, and digital assets. Copy, paste, and let Sortio do the work.
          </p>
          <div className="mt-8 text-apple-400">
            <span className="font-medium">{filteredPrompts.length}</span> prompt packs available
          </div>
        </div>

        {/* Search and Filter Controls */}
        <div className="mb-12 space-y-6">
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-apple-400" size={20} />
            <input
              type="text"
              placeholder="Search prompts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 bg-apple-700/40 border border-apple-600/30 rounded-2xl text-apple-50 placeholder-apple-400 focus:outline-none focus:ring-2 focus:ring-sortio-primary/50 focus:border-sortio-primary/50 transition-all duration-300"
            />
          </div>

          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Filter className="text-apple-400" size={20} />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="bg-apple-700/40 border border-apple-600/30 rounded-xl px-4 py-2 text-apple-50 focus:outline-none focus:ring-2 focus:ring-sortio-primary/50"
              >
                {promptCategories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
              {(searchQuery || selectedCategory !== 'All') && (
                <button
                  onClick={clearFilters}
                  className="text-sortio-primary hover:text-sortio-primary/80 text-sm font-medium transition-colors"
                >
                  Clear Filters
                </button>
              )}
            </div>

            <div className="flex items-center gap-2 bg-apple-700/40 rounded-xl p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg transition-all duration-200 ${
                  viewMode === 'grid'
                    ? 'bg-sortio-primary text-white'
                    : 'text-apple-400 hover:text-apple-50'
                }`}
              >
                <Grid size={18} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg transition-all duration-200 ${
                  viewMode === 'list'
                    ? 'bg-sortio-primary text-white'
                    : 'text-apple-400 hover:text-apple-50'
                }`}
              >
                <List size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Prompts Display */}
        {filteredPrompts.length === 0 ? (
          <div className="text-center py-16">
            <MessageSquare className="mx-auto mb-4 text-apple-400" size={48} />
            <h3 className="text-xl font-medium text-apple-50 mb-2">No prompts found</h3>
            <p className="text-apple-400">Try adjusting your search or filter criteria</p>
          </div>
        ) : (
          <div className={
            viewMode === 'grid'
              ? 'grid md:grid-cols-2 lg:grid-cols-3 gap-6'
              : 'space-y-4'
          }>
            {filteredPrompts.map((prompt) => (
              <Link
                key={prompt.id}
                to={`/prompts/${prompt.slug}`}
                className={`group block ${
                  viewMode === 'grid'
                    ? 'bg-apple-700/40 backdrop-blur-md p-6 rounded-2xl border border-apple-600/30 hover:border-sortio-primary/50 transition-all duration-300 hover:translate-y-[-4px]'
                    : 'bg-apple-700/40 backdrop-blur-md p-4 rounded-xl border border-apple-600/30 hover:border-sortio-primary/50 transition-all duration-300 flex items-center justify-between'
                }`}
              >
                <div className={viewMode === 'grid' ? '' : 'flex-1'}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-medium text-apple-50 group-hover:text-sortio-primary transition-colors">
                      {prompt.title}
                    </h3>
                    {viewMode === 'grid' && (
                      <ArrowRight className="text-apple-400 group-hover:text-sortio-primary transition-colors" size={16} />
                    )}
                  </div>
                  <div className="mb-3 flex items-center gap-2">
                    <span className="inline-block px-2 py-1 bg-sortio-primary/10 text-sortio-primary text-xs font-medium rounded-lg">
                      {prompt.category}
                    </span>
                    <span className="text-apple-400 text-xs">
                      {prompt.prompts.length} prompts
                    </span>
                  </div>
                  <p className="text-apple-300 text-sm leading-relaxed">
                    {prompt.shortDescription}
                  </p>
                </div>
                {viewMode === 'list' && (
                  <ArrowRight className="text-apple-400 group-hover:text-sortio-primary transition-colors ml-4" size={16} />
                )}
              </Link>
            ))}
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-24 text-center">
          <div className="bg-gradient-to-r from-sortio-primary/10 to-sortio-secondary/10 rounded-3xl p-8 border border-sortio-primary/20">
            <Sparkles className="mx-auto mb-4 text-sortio-primary" size={32} />
            <h3 className="text-2xl font-semibold text-apple-50 mb-4">
              Ready to Use These Prompts?
            </h3>
            <p className="text-apple-300 mb-6 max-w-2xl mx-auto">
              Download Sortio and start organizing your files with AI-powered prompts today.
            </p>
            <Link
              to="/download"
              className="inline-flex items-center px-8 py-3 bg-sortio-primary hover:bg-sortio-primary/90 text-white font-medium rounded-xl transition-colors duration-200"
            >
              Try Sortio Free
              <ArrowRight className="ml-2" size={16} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromptsPage;
